import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { ClockIcon, CalendarIcon, MicrophoneIcon, SpeakerphoneIcon, UserGroupIcon } from '@heroicons/react/solid'

import { TippyTooltip } from '@/shared/ToolTip'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { SVGIcon } from '@/components/icon'
import PublicActivity from '@/pages/Users/PublicProfile/PublicActivity'
import PublicUserRow from '@/pages/Users/PublicProfile/PublicUserRow'
import Section from '@/pages/Users/PublicProfile/Section'
import { SidebarAd } from '@/Layout/Ads'

// Order matters: the ones people actually click first.
const LINKS = [
  ['website', 'linktree', 'Website'], ['x', 'x', 'X'], ['bluesky', 'bluesky', 'Bluesky'], ['instagram', 'instagram', 'Instagram'],
  ['tiktok', 'tiktok', 'TikTok'], ['youtube', 'youtube', 'YouTube'], ['twitch', 'twitch', 'Twitch'], ['discord', 'discord', 'Discord'],
  ['soundcloud', 'soundcloud', 'SoundCloud'], ['spotify', 'spotify', 'Spotify'], ['imdb', 'imdb', 'IMDb'], ['linkedin', 'linkedin', 'LinkedIn'],
  ['artstation', 'artstation', 'ArtStation'], ['deviantart', 'deviantart', 'DeviantArt'], ['github', 'github', 'GitHub'],
  ['tumblr', 'tumblr', 'Tumblr'], ['facebook', 'facebook', 'Facebook'], ['kofi', 'kofi', 'Tips and subscriptions']
]

const SocialLink = ({ url, icon, text }) => {
  const [, setToast] = useGlobalState('toast')
  if (!url) { return null }
  const cls = 'inline-flex h-9 w-9 items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-cccblue/10 hover:text-cccblue dark:bg-gray-700 dark:text-gray-200'
  if (icon === 'discord') {
    return <CopyToClipboard text={url} onCopy={() => setToast(<div className='ml-3 w-0 flex-1 pt-0.5'><p className='text-sm font-medium text-green-600 dark:text-green-300'>Copied</p><p className='mt-1 text-sm text-green-500 dark:text-green-300'>Discord name copied. Go say hi.</p></div>)}>
      <button type='button' title={`Discord: ${url}`} aria-label='Copy Discord name' className={cls}><SVGIcon name={icon} className='h-5 w-5' /></button>
    </CopyToClipboard>
  }
  const href = icon === 'x' ? `https://x.com/${url.replace(/^@/, '')}` : url
  return <a target='_blank' href={href} rel='noreferrer' title={text} aria-label={text} className={cls}><SVGIcon name={icon} className='h-5 w-5' /></a>
}

const Fact = ({ icon: Icon, children }) => (
  <div className='flex items-center gap-x-2 text-sm text-gray-700 dark:text-gray-200'><Icon className='h-4 w-4 flex-shrink-0 text-gray-400' />{children}</div>
)

const Achievements = () => {
  const [profileContainer] = useGlobalState('profileContainer')
  const { getRequest } = useQuery()
  const [achievements, setAchievements] = useState([])
  const { user } = profileContainer

  useEffect(() => {
    getRequest(`/api/v3/users/${user.username}/achievements`, {}, (err, jsonData) => {
      if (err) { return }
      setAchievements(jsonData.achievements || [])
    })
  }, [user.username])

  if (achievements.length === 0) { return null }
  return <Section title='Achievements'>
    <div className='flex flex-wrap gap-1.5'>
      { achievements.map((a) => (
        <TippyTooltip key={`ach${a.id}`} content={<div className='flex flex-col items-center'><img src={a.imageUrl} className='h-12 w-12' alt='' /><div className='font-medium text-cccorange dark:text-cccpurple'>{a.text}</div><p>{a.description}</p></div>}>
          <span className='inline-flex items-center rounded-md bg-gray-100 p-1 dark:bg-gray-700'><img src={a.imageUrl} className='h-6 w-6' alt={a.text} /></span>
        </TippyTooltip>
      ))}
    </div>
  </Section>
}

const FeaturedUsers = ({ profileUser }) => {
  const [featuredUsers, setFeaturedUsers] = useState([])
  const { getRequest } = useQuery()
  useEffect(() => {
    if (!profileUser.showFeaturedMembers) { return }
    getRequest('/api/v3/users/featured', {}, (err, jsonData) => {
      if (err) { return }
      setFeaturedUsers(jsonData.featuredUsers || [])
    })
  }, [profileUser.id])
  if (featuredUsers.length === 0) { return null }
  return <Section title='Featured members'>
    <ul className='space-y-3'>{ featuredUsers.map((u) => <PublicUserRow key={`fuser${u.id}`} user={u} followingUserIds={[]} />) }</ul>
  </Section>
}

// Facts, links, then the things that scroll: activity, achievements, featured
// members (only when this profile is thin), the ad.
const Sidebar = () => {
  const [profileContainer] = useGlobalState('profileContainer')
  const { user, demos } = profileContainer
  const links = LINKS.filter(([key]) => user.socialLinks?.[key])
  const thin = (demos || []).length === 0 && !(user.description || '').replace(/<[^>]*>/g, '').trim()

  return <aside className='col-span-3 space-y-4 sm:col-span-1'>
    <Section title='Details'>
      <div className='space-y-2'>
        <Fact icon={CalendarIcon}>Joined {user.createdAt}</Fact>
        <Fact icon={ClockIcon}>Last seen {user.lastSeenAt} ago</Fact>
        { user.auditionsCount > 0 && <Fact icon={MicrophoneIcon}>{user.auditionsCount} {user.auditionsCount === 1 ? 'submission' : 'submissions'}</Fact> }
        { user.projectsCount > 0 && <Fact icon={SpeakerphoneIcon}>{user.projectsCount} {user.projectsCount === 1 ? 'project' : 'projects'} listed</Fact> }
        { user.affiliatesCount > 0 && <Fact icon={UserGroupIcon}>{user.affiliatesCount} members invited</Fact> }
      </div>
      { links.length > 0 && <div className='mt-4 flex flex-wrap gap-1.5'>
        { links.map(([key, icon, text]) => <SocialLink key={key} url={user.socialLinks[key]} icon={icon} text={text} />) }
      </div> }
    </Section>
    { user.canShowActivity && <PublicActivity /> }
    { user.canShowAchievements && <Achievements /> }
    { thin && <FeaturedUsers profileUser={user} /> }
    <SidebarAd adID={108} />
  </aside>
}

export default Sidebar

FeaturedUsers.propTypes = { profileUser: PropTypes.object }
SocialLink.propTypes = { url: PropTypes.string, icon: PropTypes.string, text: PropTypes.string }
